import { Component, OnInit } from '@angular/core';
import {
    LyfisService,
    LyfisScorecard,
    LyfisScorecardGridRow,
} from '../../../services/lyfis.service';
import { ToastService } from 'projects/app-core/src/app/services/toastr.service';
import { AnyoPacksGridRequest } from '../../../models/packs/AnyoPacksGridRequest';

interface CompileError {
    code: string;
    sheet?: string;
    row?: string;
    item?: string;
    currentValue?: string;
    fix?: {
        suggestion?: string;
        newValue?: string;
        explanation?: string;
    };
    details?: string;
}

interface CompileLog {
    timestamp?: string | Date;
    status?: string;
    rulesVersionId?: string;
    errors?: CompileError[];
    errorCount?: number;
}

interface UiCompileLog extends CompileLog {
    errorCount: number;
    errors: CompileError[];
}

interface LogsByRuleVersion {
    rulesVersionId: string;
    logs: UiCompileLog[];
    hasRejected: boolean;
    totalErrors: number;
}

interface ScorecardsByRuleVersion {
    rulesVersionId: string;
    scorecards: LyfisScorecardGridRow[];
    totalCount: number;
    hasSuccessful: boolean;
    hasRejected: boolean;
}

interface CompilationDetails {
    scorecard: LyfisScorecard;
    compiledArtifact?: LyfisScorecard['compiledArtifact'];
    compileLogs: UiCompileLog[];
    chatMessages: Array<{
        _id?: string;
        agentName: string;
        message: string;
        timestamp: string;
        role: string;
    }>;
}

@Component({
    selector: 'app-scorecard-compilations',
    templateUrl: './scorecard-compilations.component.html',
})
export class ScorecardCompilationsComponent implements OnInit {
    loading = false;
    scorecards: LyfisScorecardGridRow[] = [];
    groupedScorecards: ScorecardsByRuleVersion[] = [];
    selected?: CompilationDetails;
    selectedChat: CompilationDetails['chatMessages'] = [];
    drawerVisible = false;
    latestRejectedErrors: CompileError[] = [];
    groupedLogs: LogsByRuleVersion[] = [];

    constructor(
        private lyfisService: LyfisService,
        private toast: ToastService,
    ) { }

    ngOnInit(): void {
        this.loadScorecards();
    }

    async loadScorecards() {
        this.loading = true;
        try {
            const request = { page: 1, pageSize: 20 } as unknown as AnyoPacksGridRequest;
            const res = await this.lyfisService
                .listScorecards(request)
                .toPromise();
            const grid = res as { data?: LyfisScorecardGridRow[] };
            const allScorecards = grid?.data || [];
            
            // Group scorecards by rulesVersionId
            const scorecardsByVersion = new Map<string, LyfisScorecardGridRow[]>();
            allScorecards.forEach(scorecard => {
                const versionId = scorecard.rulesVersionId || 
                                scorecard.compiledArtifact?.rulesVersionId || 
                                'no-version';
                if (!scorecardsByVersion.has(versionId)) {
                    scorecardsByVersion.set(versionId, []);
                }
                scorecardsByVersion.get(versionId)!.push(scorecard);
            });

            // Convert to array and sort by most recent scorecard in each group
            this.groupedScorecards = Array.from(scorecardsByVersion.entries()).map(([rulesVersionId, scorecards]) => {
                // Sort scorecards within group by compiledAt (most recent first)
                scorecards.sort((a, b) => {
                    const aTime = a.compiledAt ? new Date(a.compiledAt).getTime() : 0;
                    const bTime = b.compiledAt ? new Date(b.compiledAt).getTime() : 0;
                    return bTime - aTime;
                });
                
                return {
                    rulesVersionId,
                    scorecards,
                    totalCount: scorecards.length,
                    hasSuccessful: scorecards.some(s => s.compileStatus === 'ok'),
                    hasRejected: scorecards.some(s => s.compileStatus === 'rejected'),
                };
            }).sort((a, b) => {
                // Sort groups by most recent scorecard
                const aLatest = a.scorecards[0]?.compiledAt ? new Date(a.scorecards[0].compiledAt).getTime() : 0;
                const bLatest = b.scorecards[0]?.compiledAt ? new Date(b.scorecards[0].compiledAt).getTime() : 0;
                return bLatest - aLatest;
            });

            // Keep flat array for backward compatibility if needed
            this.scorecards = allScorecards;
        } catch (err) {
            console.error(err);
            this.toast.showAnyoErrorToast('Failed to load scorecards');
        } finally {
            this.loading = false;
        }
    }

    async viewCompilation(scorecard: LyfisScorecard) {
        try {
            const details = await this.lyfisService
                .getScorecardCompilation(scorecard.scorecardId)
                .toPromise();
            if (details) {
                const rawLogs = Array.isArray(details.compileLogs)
                    ? details.compileLogs
                    : [];

                const normalizedLogs: UiCompileLog[] = rawLogs.map((log) => {
                    const rec = log as Record<string, unknown>;
                    const errorsRaw =
                        (log as any).errors ?? rec['errors'];
                    const errors = Array.isArray(errorsRaw)
                        ? (errorsRaw as CompileError[])
                        : [];

                    return {
                        timestamp: (log as any).timestamp ?? rec['timestamp'],
                        status: (log as any).status ?? rec['status'],
                        rulesVersionId: (log as any).rulesVersionId ?? rec['rulesVersionId'] ?? 'unknown',
                        errors,
                        errorCount: errors.length,
                    };
                });

                // Group logs by rulesVersionId
                const logsByVersion = new Map<string, UiCompileLog[]>();
                normalizedLogs.forEach(log => {
                    const versionId = log.rulesVersionId || 'unknown';
                    if (!logsByVersion.has(versionId)) {
                        logsByVersion.set(versionId, []);
                    }
                    logsByVersion.get(versionId)!.push(log);
                });

                // Convert to array and sort by most recent log in each group
                this.groupedLogs = Array.from(logsByVersion.entries()).map(([rulesVersionId, logs]) => {
                    // Sort logs within group by timestamp (most recent first)
                    logs.sort((a, b) => {
                        const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
                        const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
                        return bTime - aTime;
                    });
                    
                    return {
                        rulesVersionId,
                        logs,
                        hasRejected: logs.some(l => l.status === 'rejected'),
                        totalErrors: logs.reduce((sum, l) => sum + l.errorCount, 0),
                    };
                }).sort((a, b) => {
                    // Sort groups by most recent log timestamp
                    const aLatest = a.logs[0]?.timestamp ? new Date(a.logs[0].timestamp).getTime() : 0;
                    const bLatest = b.logs[0]?.timestamp ? new Date(b.logs[0].timestamp).getTime() : 0;
                    return bLatest - aLatest;
                });

                // Find latest rejected errors (from first group with rejected logs)
                const latestRejected = this.groupedLogs
                    .flatMap(g => g.logs)
                    .find(l => l.status === 'rejected' && l.errorCount > 0);
                this.latestRejectedErrors = latestRejected?.errors ?? [];

                this.selected = {
                    scorecard: details.scorecard,
                    compiledArtifact: details.compiledArtifact || undefined,
                    compileLogs: normalizedLogs,
                    chatMessages: details.chatMessages || [],
                };
                this.selectedChat = details.chatMessages || [];
                this.drawerVisible = true;
            }
        } catch (err) {
            console.error('Error fetching compilation details:', err);
            this.toast.showAnyoErrorToast('Failed to fetch compilation details');
        }
    }

    closeDrawer() {
        this.drawerVisible = false;
        this.selected = undefined;
        this.selectedChat = [];
        this.latestRejectedErrors = [];
        this.groupedLogs = [];
    }

    downloadConfig() {
        if (!this.selected?.compiledArtifact?.config) {
            return;
        }
        const dataStr = JSON.stringify(this.selected.compiledArtifact.config, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `config-${this.selected.scorecard.scorecardId}.json`;
        link.click();
        URL.revokeObjectURL(url);
    }

    downloadEngine() {
        if (!this.selected?.compiledArtifact?.engineCode) {
            return;
        }
        const dataBlob = new Blob([this.selected.compiledArtifact.engineCode], { type: 'text/javascript' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `engine-${this.selected.scorecard.scorecardId}.js`;
        link.click();
        URL.revokeObjectURL(url);
    }

    async downloadExcel() {
        if (!this.selected?.scorecard?.scorecardId) {
            return;
        }
        try {
            const result = await this.lyfisService
                .getScorecardExcelDownloadUrl(this.selected.scorecard.scorecardId)
                .toPromise();
            if (result?.downloadUrl) {
                window.open(result.downloadUrl, '_blank');
            }
        } catch (err) {
            console.error(err);
            this.toast.showAnyoErrorToast('Failed to generate download URL');
        }
    }
}

